import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FirebaseUtilitiesService } from 'src/app/services/firebase-utilities.service';

interface FacilityUser {
  id: string;
  name?: string;
  email?: string;
  role?: string;
  [key: string]: any;
}

@Component({
    selector: 'app-facility-users',
    templateUrl: './facility-users.component.html',
    styleUrls: ['./facility-users.component.scss'],
    standalone: false
})
export class FacilityUsersComponent implements OnChanges {
  @Input() facilityId: string | null = null;
  @Input() facilityName: string | null = null;
  @Input() selectedUsers: FacilityUser[] = [];

  @Output() usersSelected = new EventEmitter<FacilityUser[]>();

  users: FacilityUser[] = [];
  filteredUsers: FacilityUser[] = [];
  searchQuery = '';
  isLoading = false;
  loadError = '';

  constructor(private firebaseUtilities_: FirebaseUtilitiesService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['facilityId']) {
      this.loadUsers();
    }
  }

  async loadUsers(): Promise<void> {
    if (!this.facilityId) {
      this.users = [];
      this.filteredUsers = [];
      return;
    }

    this.isLoading = true;
    this.loadError = '';
    try {
      const users = await this.firebaseUtilities_.getFacilityUsers(this.facilityId.toLowerCase().trim());
      this.users = users || [];
      this.filteredUsers = [...this.users];
    } catch (error: any) {
      const isPermissionError =
        error?.code === 'permission-denied' || error?.message?.includes('insufficient permissions') || error?.message?.includes('Missing or insufficient permissions');
      if (isPermissionError) {
        console.warn('[Facility Users] Cannot load facility users (permission denied for this facility).');
      } else {
        console.error('[Facility Users] Failed to load users:', error);
      }
      this.loadError = 'Unable to load facility users.';
      this.users = [];
      this.filteredUsers = [];
    } finally {
      this.isLoading = false;
    }
  }

  onSearchChange(): void {
    const query = this.searchQuery.toLowerCase().trim();
    if (!query) {
      this.filteredUsers = [...this.users];
      return;
    }

    this.filteredUsers = this.users.filter(user => {
      const name = (user.name || '').toLowerCase();
      const email = (user.email || '').toLowerCase();
      const role = (user.role || '').toLowerCase();
      return name.includes(query) || email.includes(query) || role.includes(query);
    });
  }

  toggleUser(user: FacilityUser): void {
    if (!user?.id) {
      return;
    }
    const alreadySelected = this.isUserSelected(user);
    const updated = alreadySelected
      ? this.selectedUsers.filter(u => u?.id !== user.id)
      : [...(this.selectedUsers || []), user];

    this.usersSelected.emit(updated);
  }

  isUserSelected(user: FacilityUser): boolean {
    return (this.selectedUsers || []).some(u => u?.id === user.id);
  }

  getRoleLabel(role?: string): string {
    if (!role) return '';
    return role === 'CLIENT_ADMIN' ? 'Facility user' : role;
  }

  trackByUserId(_index: number, user: FacilityUser): string {
    return user.id;
  }
}
